import { useState } from 'react'
import { mfaAPI } from '../../services/mfaAPI'
import Button from '../UI/Button'
import Input from '../UI/Input'
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../UI/Card'
import toast from 'react-hot-toast'
import { Shield, Copy, CheckCircle, AlertCircle } from 'lucide-react'

export default function MFASetup({ onComplete, onCancel }) {
  const [step, setStep] = useState('setup') // 'setup' or 'verify'
  const [loading, setLoading] = useState(false)
  const [mfaData, setMfaData] = useState(null)
  const [token, setToken] = useState('')
  const [backupCodes, setBackupCodes] = useState([])

  const handleSetup = async () => {
    setLoading(true)
    try {
      const response = await mfaAPI.setup()
      if (response.data.success) {
        setMfaData(response.data.data)
        setBackupCodes(response.data.data.backup_codes)
        setStep('verify')
        toast.success('MFA setup initialized')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to setup MFA')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!token || token.length !== 6) {
      toast.error('Please enter a valid 6-digit code')
      return
    }

    setLoading(true)
    try {
      const response = await mfaAPI.verify(token)
      if (response.data.success) {
        toast.success('MFA enabled successfully!')
        if (onComplete) {
          onComplete(response.data.data.backup_codes || backupCodes)
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid token. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  if (step === 'setup') {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary-600" />
            <CardTitle>Setup Multi-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Secure your account with an additional layer of protection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">How it works:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Scan the QR code with an authenticator app</li>
                    <li>Enter the 6-digit code to verify</li>
                    <li>Save your backup codes in a safe place</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSetup} loading={loading}>
            Start Setup
          </Button>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary-600" />
          <CardTitle>Verify MFA Setup</CardTitle>
        </div>
        <CardDescription>
          Scan the QR code and enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* QR Code */}
          {mfaData?.qr_code && (
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <img 
                  src={mfaData.qr_code} 
                  alt="MFA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Scan with Google Authenticator, Authy, or similar app
              </p>
            </div>
          )}

          {/* Manual Entry */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Or enter this code manually:
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={mfaData?.secret || ''}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(mfaData?.secret)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Token Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Enter 6-digit code from your app:
            </label>
            <Input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl font-mono tracking-widest"
            />
          </div>

          {/* Backup Codes */}
          {backupCodes.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-800 mb-2">
                    Save these backup codes!
                  </p>
                  <p className="text-xs text-yellow-700 mb-2">
                    You can use these codes to access your account if you lose your device.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className="font-mono text-xs bg-white p-2 rounded border border-yellow-300"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const codesText = backupCodes.join('\n')
                      copyToClipboard(codesText)
                    }}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy All Codes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button variant="outline" onClick={() => setStep('setup')} disabled={loading}>
          Back
        </Button>
        <Button onClick={handleVerify} loading={loading} disabled={token.length !== 6}>
          Verify & Enable
        </Button>
      </CardFooter>
    </Card>
  )
}

